import { useTranslation } from "react-i18next";
import React from "react";
import { LayerData } from "../../utils/brickOptimizer";
import IsometricBrickRenderer from "./IsometricBrickRenderer";
import "./legoStyles/index.css";

interface MultiStepGuideProps {
  currentPage: number;
  totalPages: number;
  bricksOnPage: LayerData["bricks"];
  allPreviousBricks: LayerData["bricks"];
  onPreviousPage: () => void;
  onNextPage: () => void;
  onTouchStart: (e: React.TouchEvent<HTMLDivElement>) => void;
  onTouchEnd: (e: React.TouchEvent<HTMLDivElement>) => void;
  bricksPerPage: number;
}

const MultiStepGuide: React.FC<MultiStepGuideProps> = ({
  currentPage,
  totalPages,
  bricksOnPage,
  allPreviousBricks,
  onPreviousPage,
  onNextPage,
  onTouchStart,
  onTouchEnd,
  bricksPerPage,
}) => {
  const { t } = useTranslation();
  if (bricksOnPage.length === 0) return null;

  const startStep = currentPage * bricksPerPage + 1;
  const endStep = startStep + bricksOnPage.length - 1;
  const progress = Math.round(((currentPage + 1) / totalPages) * 100);

  return (
    <div
      className="current-step-guide lego-manual-style"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Enhanced Navigation Bar */}
      <div className="nav-bar">
        <button
          type="button"
          onClick={onPreviousPage}
          disabled={currentPage === 0}
          className="nav-arrow"
          aria-label={t("common.previous")}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-6 w-6"
            aria-hidden="true"
          >
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>

        <div className="page-indicator">
          <div className="flex items-center gap-2 mb-1">
            <span className="page-number">{currentPage + 1}</span>
            <span className="page-divider">/</span>
            <span className="total-pages">{totalPages}</span>
          </div>
          <div className="text-xs text-slate-500 mb-1">{t("instructions.pageOf", { current: currentPage + 1, total: totalPages })}</div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ transform: `scaleX(${progress / 100})` }}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={onNextPage}
          disabled={currentPage >= totalPages - 1}
          className="nav-arrow"
          aria-label={t("common.next")}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-6 w-6"
            aria-hidden="true"
          >
            <path d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="multi-step-page">
        <div className="step-content">
          {/* Main build visualization */}
          <div className="main-build-view">
            <h4 className="text-lg font-semibold text-slate-800 mb-4">
              {allPreviousBricks.length === 0 ? t('instructions.startingBuild') : t('instructions.buildProgress', { count: allPreviousBricks.length + bricksOnPage.length })}
            </h4>
            <div className="canvas-container">
              <IsometricBrickRenderer
                bricks={[...allPreviousBricks, ...bricksOnPage]}
                width={700}
                height={500}
                scale={2.0}
              />
            </div>
            {allPreviousBricks.length === 0 && (
              <div className="first-step-hint">
                {t("instructions.startPrompt")}
              </div>
            )}
          </div>

          {/* Pieces to add */}
          <div className="pieces-to-add">
            <div className="pieces-header">
              <div>
                <h4 className="text-lg font-semibold">{t("instructions.stepsRange", { from: startStep, to: endStep })}</h4>
                <p className="text-sm text-slate-600 mt-1">{t("instructions.addThesePieces")}</p>
              </div>
              <span className="piece-count">{t("instructions.piecesCount", { count: bricksOnPage.length })}</span>
            </div>
            <div className="pieces-grid">
              {bricksOnPage.map((brick, index) => (
                <div key={index} className="piece-card">
                  <div className="piece-step-number">{startStep + index}</div>
                  <div className="piece-preview">
                    <IsometricBrickRenderer
                      bricks={[brick]}
                      width={160}
                      height={120}
                      scale={2.2}
                    />
                  </div>
                  <div className="piece-details">
                    <div className="piece-type-name">
                      {t("instructions.brickName", { w: brick.brickType.width, h: brick.brickType.height })}
                    </div>
                    <div className="piece-color-name">{brick.colorName}</div>
                    <div className="piece-position">
                      {t("instructions.rowColumn", { row: brick.y + 1, column: brick.x + 1 })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-brick/20">
              <div className="flex items-center justify-center gap-2 text-sm text-ink-muted">
                <span className="text-brick font-bold">{t("instructions.tipLabel")}</span>
                <span>{t("instructions.clickHint")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiStepGuide;
